// src/modules/requerimiento/views/mantenimiento/TrabajoCampo.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Alert, Card, CardContent, CircularProgress,
  Stack, Divider, Button, Grid, CardActionArea,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useParams }                               from "react-router-dom";
import { WarningAmberRounded as WarningIcon }      from "@mui/icons-material";
import CheckCircleIcon                             from "@mui/icons-material/CheckCircle";
import ArrowDropUpIcon                             from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon                           from "@mui/icons-material/ArrowDropDown";
import { green, red }                              from "@mui/material/colors";
import RequerimientoHeader                         from "../../components/RequerimientoHeader";
import AsignadoModal                               from "../../components/mantenimiento/AsignadoModal";
import AgregarEquiposDialog                        from "../../components/mantenimiento/AgregarEquiposDialog";
import AddIcon                                     from "@mui/icons-material/Add";
import BuildIcon                                   from "@mui/icons-material/Build";
import { getMantenimientoAsignados }               from "../../services/mantenimiento";
import { updateRequerimiento }                     from "../../services/reqSolicitudes";

// ── Helpers fuera del componente ──────────────────────────────
const ESTADOS_BLOQUEADOS = ["Espera", "Activo", "Vendido Activo", "Inactivo"];

function isItemBlocked(it) {
  return it?.estado_pendiente === "Remplazo Devuelto" ||
    ESTADOS_BLOQUEADOS.includes(it?.estado ?? "");
}

function splitObs(obs) {
  return (obs ?? "").split("&").map((s) => s.trim()).filter(Boolean);
}

// ── Indicador lateral de la card ──────────────────────────────
const ESTADOS_OK = ["Inactivo","Baja","Mantenimiento","Espera","Activo","Vendido Activo"];

function CardIndicador({ estado }) {
  const esOk = ESTADOS_OK.includes(estado);
  return (
    <Box sx={{ width: 151, flexShrink: 0, height: "100%", backgroundColor: esOk ? "green" : "orange",
      display: "flex", justifyContent: "center", alignItems: "center" }}>
      {esOk
        ? <CheckCircleIcon sx={{ fontSize: 50, color: "white" }} />
        : <WarningIcon      sx={{ fontSize: 50, color: "white" }} />}
    </Box>
  );
}

// ── AsignadoCard fuera del componente ────────────────────────
// FIX: definir fuera para que no se recree en cada render del padre
function AsignadoCard({ item, onOpenModal }) {
  const {
    num_sim, id, placa, modelo, emai, observacion,
    estado, estado_pendiente, modem_afectado,
    numero_chip, chips_actuales = [], chips_historial = [],
  } = item || {};

  const bloqueado     = isItemBlocked(item);
  const chipPrincipal = numero_chip ?? "—";
  const obsParts      = splitObs(observacion);

  return (
    <Card sx={{ display: "flex" }}>
      <CardActionArea
        sx={{ display: "flex", cursor: bloqueado ? "not-allowed" : "pointer", opacity: bloqueado ? 0.75 : 1 }}
        onClick={() => { if (!bloqueado) onOpenModal(num_sim); }}
        aria-disabled={bloqueado || undefined}
        title={bloqueado ? "No disponible para abrir modal en este estado" : "Ver detalles"}
      >
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <CardContent sx={{ flex: "1 0 auto" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}>
              {num_sim || "—"}
            </Typography>

            <Stack spacing={0.5}>
              <Typography variant="body2"><strong>ID:</strong> {id ?? num_sim ?? "—"}</Typography>
              <Typography variant="body2"><strong>Placa:</strong> {placa || "—"}</Typography>
              <Typography variant="body2"><strong>Chip principal:</strong> {chipPrincipal}</Typography>

              {chips_actuales.length > 0 && (
                <Typography variant="body2">
                  <strong>Chip(s) actual(es):</strong> {chips_actuales.join(", ")}
                </Typography>
              )}

              {chips_historial.length > 0 && (
                <Typography variant="body2">
                  <strong>Chip(s) Remplazados:</strong>{" "}
                  {chips_historial.map((c, i) => {
                    if (c.estado_pendiente === "Remplazo Adicion") {
                      return (
                        <Box key={`add-${c.numero}-${i}`} component="span"
                          sx={{ color: "success.main", fontWeight: "bold", display: "inline-flex", alignItems: "center", mr: 0.75 }}>
                          <ArrowDropUpIcon sx={{ fontSize: 18, mr: 0.25 }} />{c.numero}
                        </Box>
                      );
                    }
                    if (c.estado_pendiente === "Remplazo Devuelto") {
                      return (
                        <Box key={`dev-${c.numero}-${i}`} component="span"
                          sx={{ color: "error.main", fontWeight: "bold", display: "inline-flex", alignItems: "center", mr: 0.75 }}>
                          <ArrowDropDownIcon sx={{ fontSize: 18, mr: 0.25 }} />{c.numero}
                        </Box>
                      );
                    }
                    if (c.estado_pendiente === "" || c.estado_pendiente == null) return null;
                    return <Box key={`otro-${c.numero}-${i}`} component="span" sx={{ mr: 0.75 }}>{c.numero}</Box>;
                  })}
                </Typography>
              )}

              {chips_historial.some((c) => (c.estado_pendiente ?? "") === "") && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Chip(s) Devuelto(s):</strong>{" "}
                  {chips_historial.filter((c) => (c.estado_pendiente ?? "") === "").map((c, i) => (
                    <Box key={`dev-vacio-${c.numero}-${i}`} component="span"
                      sx={{ color: "error.main", fontWeight: "bold", display: "inline-flex", alignItems: "center", mr: 0.75 }}>
                      <ArrowDropDownIcon sx={{ fontSize: 18, mr: 0.25 }} />{c.numero}
                    </Box>
                  ))}
                </Typography>
              )}

              <Typography variant="body2">
                <strong>Modelo:</strong> {modelo || "—"} <span style={{ opacity: 0.5 }}>•</span>{" "}
                <strong>IMEI/EMAI:</strong> {emai || "—"}
              </Typography>

              {obsParts.length > 0 && (
                <Typography variant="body2"><strong>Observación:</strong> {obsParts.join(", ")}</Typography>
              )}

              {estado_pendiente === "Remplazo Devuelto" ? (
                <Typography variant="body2" textAlign="right" sx={{ color: red[500], fontWeight: "bold" }}>
                  <strong>Modem Reemplazado</strong>
                </Typography>
              ) : estado_pendiente === "Remplazo Adicion" ? (
                <Typography variant="body2" textAlign="right" sx={{ color: green[500], fontWeight: "bold" }}>
                  <strong>Reemplazo del modem: {modem_afectado || "—"}</strong>
                </Typography>
              ) : estado === "Inactivo" ? (
                <Typography variant="body2" textAlign="right" sx={{ color: red[500], fontWeight: "bold" }}>
                  <strong>MODEM DEVUELTO</strong>
                </Typography>
              ) : null}
            </Stack>
          </CardContent>
        </Box>

        <CardIndicador estado={estado} />
      </CardActionArea>
    </Card>
  );
}

// ── Componente principal ──────────────────────────────────────
export default function MantenimientoTrabajoCampo(props) {
  const params = useParams();
  const cod    = useMemo(() => props?.cod ?? params?.cod, [props?.cod, params?.cod]);

  const [asignados,        setAsignados]        = useState([]);
  const [loadingAsignados, setLoadingAsignados] = useState(false);
  const [errAsignados,     setErrAsignados]     = useState(null);

  const blockedCount = useMemo(() => asignados.filter(isItemBlocked).length, [asignados]);
  const allBlocked   = asignados.length > 0 && blockedCount === asignados.length;

  const fetchAsignados = useCallback(async () => {
    if (!cod) return;
    setLoadingAsignados(true); 
    setErrAsignados(null);
    
    try {
      const { data: responseBody } = await getMantenimientoAsignados(cod);
      
      // De acuerdo a tu JSON, los datos reales están en data.data
      // Pero agregamos una validación por si el interceptor ya los aplanó
      const rawData = responseBody?.data || responseBody;
  
      if (Array.isArray(rawData)) {
        setAsignados(rawData);
      } else {
        console.error("Estructura de API no reconocida", responseBody);
        setAsignados([]);
      }
    } catch (e) {
      setErrAsignados(e?.response?.data?.error || e.message || "Error cargando asignados");
      setAsignados([]); // Reset de seguridad
    } finally { 
      setLoadingAsignados(false); 
    }
  }, [cod]);

  useEffect(() => { fetchAsignados(); }, [fetchAsignados]);

  // ── Finalizar — Dialog en lugar de window.confirm ─────────
  const [openFinalizar,  setOpenFinalizar]  = useState(false);
  const [finalizeLoading,setFinalizeLoading]= useState(false);
  const [finalizeErr,    setFinalizeErr]    = useState(null);
  const [finalizeOk,     setFinalizeOk]     = useState(null);

  const handleFinalizar = async () => {
    if (!cod) { setFinalizeErr("Falta el código del requerimiento."); return; }
    setFinalizeLoading(true); setFinalizeErr(null); setFinalizeOk(null);
    try {
      await updateRequerimiento(String(cod), { estado: 7 });
      setFinalizeOk("Requerimiento avanzado a Revisión en Sistemas.");
      setOpenFinalizar(false);
      await props?.onRefreshRequerimiento?.();
      await fetchAsignados();
    } catch (e) {
      setFinalizeErr(e?.response?.data?.error || e.message || "No se pudo actualizar el estado.");
    } finally { setFinalizeLoading(false); }
  };

  // ── Agregar equipos (antes era la etapa de Programación) ──
  const [agregarOpen, setAgregarOpen] = useState(false);

  const handleAgregados = useCallback(async () => {
    // Mantenimiento emitido antes del cambio (aún en Coordinación/Programación): con los primeros
    // equipos agregados pasa a Trabajo de campo. Si falla, los equipos ya quedaron asignados.
    if ([1, 2].includes(Number(props?.estado))) {
      await updateRequerimiento(String(cod), { estado: 3 }).catch(() => {});
    }
    await fetchAsignados();
    await props?.onRefreshRequerimiento?.();
  }, [cod, props, fetchAsignados]);

  // ── Modal ─────────────────────────────────────────────────
  const [modalOpen,      setModalOpen]      = useState(false);
  const [selectedNumSim, setSelectedNumSim] = useState(null);

  const openModal  = useCallback((numSim) => { setSelectedNumSim(numSim); setModalOpen(true); }, []);
  const closeModal = useCallback(() => { setModalOpen(false); setSelectedNumSim(null); }, []);

  const handleRefreshModal = useCallback(async () => {
    await props?.onRefreshRequerimiento?.();
    await fetchAsignados();
  }, [props, fetchAsignados]);

  return (
    <Box className="p-4" sx={{ pb: 6 }}>
      {finalizeOk  && <Alert severity="success" onClose={() => setFinalizeOk(null)}  sx={{ mb: 2 }}>{finalizeOk}</Alert>}
      {finalizeErr && <Alert severity="error"   onClose={() => setFinalizeErr(null)} sx={{ mb: 2 }}>{finalizeErr}</Alert>}

      <RequerimientoHeader cod={cod} />

      {/* Equipos del mantenimiento: se agregan desde las placas del requerimiento */}
      <Card variant="outlined" sx={{ borderRadius: "10px", mb: 2 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "8px", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0, background: "linear-gradient(135deg, #D97706, #B45309)" }}>
              <BuildIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Equipos a revisar</Typography>
              <Typography variant="body2" color="text.secondary">
                {loadingAsignados
                  ? "Cargando…"
                  : asignados.length === 0
                    ? "Agrega los equipos de las placas registradas en el requerimiento."
                    : `${blockedCount} de ${asignados.length} ${asignados.length === 1 ? "equipo atendido" : "equipos atendidos"}`}
              </Typography>
            </Box>
            <Button
              variant={asignados.length === 0 ? "contained" : "outlined"}
              startIcon={<AddIcon />}
              onClick={() => setAgregarOpen(true)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {asignados.length === 0 ? "Agregar equipos" : "Agregar más equipos"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Asignados al requerimiento</Typography>

      {loadingAsignados ? (
        <Stack direction="row" alignItems="center" gap={1}><CircularProgress size={20} /> Cargando asignados...</Stack>
      ) : errAsignados ? (
        <Alert severity="error">{errAsignados}</Alert>
      ) : asignados.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No hay equipos asignados todavía.</Typography>
      ) : (
        <Grid container spacing={2}>
          {asignados.map((row) => (
            // FIX: key con num_sim en lugar de índice compuesto
            <Grid item size={{ xs: 12 }} key={row.num_sim}>
              <AsignadoCard item={row} onOpenModal={openModal} />
            </Grid>
          ))}
        </Grid>
      )}

      <AgregarEquiposDialog
        open={agregarOpen}
        cod={cod}
        onClose={() => setAgregarOpen(false)}
        onAgregados={handleAgregados}
        onToast={props?.onToast}
      />

      <AsignadoModal
        open={modalOpen}
        onClose={closeModal}
        cod={cod}
        numSim={selectedNumSim}
        // FIX: console.log eliminado — callbacks recibidos como props desde el padre si se necesitan
        onSelect={props?.onSelectAction ?? undefined}
        onRefreshRequerimiento={handleRefreshModal}
      />

      <Divider sx={{ mt: 3, mb: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, pb: 2 }}>
        <Button variant="contained" color="secondary"
          onClick={() => setOpenFinalizar(true)}
          disabled={finalizeLoading || !allBlocked}
          sx={{ minWidth: 220 }}>
          {finalizeLoading ? "Guardando..." : "Finalizar trabajo de campo"}
        </Button>
      </Box>

      {/* FIX: Dialog en lugar de window.confirm */}
      <Dialog open={openFinalizar} onClose={() => !finalizeLoading && setOpenFinalizar(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Finalizar mantenimiento</DialogTitle>
        <DialogContent>
          <Typography>¿Finalizar el mantenimiento? El requerimiento avanzará a <strong>Revisión en Sistemas</strong>.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFinalizar(false)} disabled={finalizeLoading}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleFinalizar} disabled={finalizeLoading}>
            {finalizeLoading ? "Guardando..." : "Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}